// ═══════════════════════════════════════════════════════════════
// Handoff — Events List
// Shows incidents from the shared Ignatius incidents table
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, TextInput, Select,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useHandoffStore } from '../../store/handoffStore';
import { useState } from 'react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

export default function Events() {
  const navigate = useNavigate();
  const { incidents, loadIncidents } = useAuthStore();
  const { notes, actionItems, loadNotes, loadActionItems } = useHandoffStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadIncidents();
    loadNotes();
    loadActionItems();
  }, []);

  const filtered = incidents
    .filter(e => statusFilter === 'all' || e.status === statusFilter)
    .filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()));

  const statusColor = (s: string) =>
    s === 'active' ? 'green' : s === 'monitoring' ? 'yellow' : s === 'closed' ? 'gray' : 'gray';

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Incidents</Title>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search incidents..."
          leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)}
          size="xs" w={240}
        />
        <Select data={STATUS_OPTIONS} value={statusFilter} onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={140} />
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No incidents found.</Text>
        </Paper>
      ) : (
        <Stack gap="sm">
          {filtered.map(inc => {
            const noteCount = notes.filter(n => String(n.eventId) === String(inc.id)).length;
            const pendingActions = actionItems.filter(a => String(a.eventId) === String(inc.id) && a.status === 'pending').length;
            return (
              <Paper key={inc.id} p="md" radius="sm" withBorder
                style={{ cursor: 'pointer' }} onClick={() => navigate(`/handoff/event/${inc.id}`)}>
                <Group justify="space-between">
                  <div>
                    <Group gap="xs" mb={4}>
                      <Text fw={600}>{inc.name}</Text>
                      <Badge size="xs" color={statusColor(inc.status)} tt="capitalize">{inc.status}</Badge>
                      <Badge size="xs" variant="outline" color="gray" tt="capitalize">{inc.incidentType}</Badge>
                    </Group>
                    {inc.description && <Text size="xs" c="dimmed" lineClamp={1}>{inc.description}</Text>}
                  </div>
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="blurple">{noteCount} handoffs</Badge>
                    {pendingActions > 0 && <Badge size="xs" variant="light" color="cyan">{pendingActions} actions</Badge>}
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
